import React, { useMemo } from 'react';
import { View } from 'react-native';
import { rem } from 'design-system';
import useStyles from 'hooks/useStyles';
import useColors from 'hooks/useColors';
import { Avatar } from 'design-system/avatar';
import useColorScheme from 'hooks/useColorScheme';
import { extractImage } from 'utils/imageHelpers';
import createStyleSheets from 'utils/createStyleSheets';
import Logo from 'design-system/Logo';
import IconCategoryExcluded from 'features/transactions/icons/categories/IconCategoryExcluded';
import { CREDIT_REPORT_PROVIDER } from 'utils/creditReportAccounts';
import EmojiText from './EmojiText';
import IconCircle from './icons/IconCircle';
const fallbackSourceLight = require('./img/bank-icon.png');
const fallbackSourceDark = require('./img/bank-icon-dark.png');
export const CIRCLE_SIZE = 44;
export const CIRCLE_BORDER_SIZE = 1;
const LogoBankAccount = ({ data, onCard, showHidden = false, size, onColoredBackground, }) => {
    const colors = useColors();
    const colorScheme = useColorScheme();
    const circleSize = circleSizeMap[size];
    const emojiFontSize = emojiFontSizeMap[size];
    const borderWidth = borderWidthMap[size];
    const styleSet = useMemo(() => getStyleSet(circleSize, borderWidth), [borderWidth, circleSize]);
    const styles = useStyles(styleSet);
    const fallbackContainerStyle = useMemo(() => [
        styles.fallbackContainerWrapper,
        onCard && styles.onCard,
        onColoredBackground && styles.onColoredBackground,
    ], [onCard, onColoredBackground, styles]);
    if ('emoji' in data && data.emoji) {
        return (<View style={fallbackContainerStyle}>
        <IconCircle color={onCard ? colors.background.dark : colors.background.light} width={circleSize} height={circleSize}>
          <EmojiText fontSize={emojiFontSize}>{data.emoji}</EmojiText>
        </IconCircle>
      </View>);
    }
    if (showHidden && 'isHidden' in data && data.isHidden) {
        return (<View style={fallbackContainerStyle}>
        <IconCircle color={onCard ? colors.background.dark : colors.background.light} width={circleSize} height={circleSize}>
          <IconCategoryExcluded width={rem(24)} height={rem(24)} fill={colors.text.secondary} viewBox="0 0 24 24"/>
        </IconCircle>
      </View>);
    }
    if ((data.isManual ||
        data.provider === CREDIT_REPORT_PROVIDER) &&
        !data.customIconUrl) {
        return (<View style={fallbackContainerStyle}>
        <Avatar name={data.name} color={colors.background.brandLight} size={circleSize} borderWidth={borderWidth > 2 ? 2 : 1} fontColor={colors.text.textOnBrand} style={styles.avatar} medium={size !== 'R64'} large={size === 'R64'}/>
      </View>);
    }
    const source = extractImage(data, showHidden);
    const uri = source && typeof source === 'object' ? source.uri : null;
    const fallbackSource = colorScheme === 'dark' ? fallbackSourceDark : fallbackSourceLight;
    return (<Logo {...{ [size]: true }} uri={uri} fallbackSource={fallbackSource} transparent onColoredBackground={onColoredBackground}/>);
};
const circleSizeMap = {
    R24: rem(24),
    R40: rem(40),
    R44: rem(44),
    R56: rem(56),
    R64: rem(52),
};
const emojiFontSizeMap = {
    R24: rem(12),
    R40: rem(20),
    R44: rem(20),
    R56: rem(26),
    R64: rem(26),
};
const borderWidthMap = {
    R24: rem(1),
    R40: rem(1),
    R44: rem(1),
    R56: rem(1),
    R64: rem(6),
};
const getStyleSet = (circleSize, borderWidth) => createStyleSheets((colors) => ({
    container: {
        width: circleSize,
        height: circleSize,
        borderRadius: circleSize / 2,
        backgroundColor: colors.cards.onLight,
        justifyContent: 'center',
        alignItems: 'center',
    },
    onCard: {
        backgroundColor: colors.cards.onDark,
    },
    onColoredBackground: {
        backgroundColor: colors.background.transparent, // same as Logo's style when onColoredBackground
    },
    fallbackContainerWrapper: {
        width: circleSize + borderWidth * 2,
        height: circleSize + borderWidth * 2,
        borderRadius: circleSize / 2 + borderWidth,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: colors.cards.onLight,
    },
    avatar: {
        borderColor: colors.borders.logoStroke,
    },
}));
export default LogoBankAccount;
